import React, { useState, useEffect } from 'react';
import { PREDEFINED_TAGS } from '../data/predefinedTags';

const TagManager = ({ currentTags = [], onTagsChange, onClose, isOpen }) => {
  const [selectedTags, setSelectedTags] = useState(currentTags);
  const [customTag, setCustomTag] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setSelectedTags(currentTags);
    }
  }, [isOpen, currentTags]);

  if (!isOpen) return null;

  const toggleTag = (tagId) => {
    if (selectedTags.includes(tagId)) {
      setSelectedTags(selectedTags.filter(id => id !== tagId));
    } else {
      // Ограничиваем до 3 тегов
      if (selectedTags.length < 3) {
        setSelectedTags([...selectedTags, tagId]);
      }
    }
  };

  const handleAddCustomTag = () => {
    if (customTag.trim() && selectedTags.length < 3) {
      const customTagId = `custom_${customTag.toLowerCase().replace(/\s+/g, '_')}`;
      setSelectedTags([...selectedTags, customTagId]);
      setCustomTag('');
      setShowCustomInput(false);
    }
  };

  const handleSave = () => {
    onTagsChange(selectedTags);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Заголовок */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center">
              <svg className="w-7 h-7 mr-2 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
              </svg>
              Управление тегами
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Информация */}
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>💡 Совет:</strong> Добавьте до 3 тегов для организации ваших подписок
            </p>
          </div>

          {/* Счетчик */}
          <div className="mb-4 flex items-center justify-between">
            <span className="text-sm text-gray-600">
              Выбрано: <span className="font-semibold">{selectedTags.length}/3</span>
            </span>
            {selectedTags.length > 0 && (
              <button
                onClick={() => setSelectedTags([])}
                className="text-xs text-red-600 hover:text-red-700"
              >
                Очистить все
              </button>
            )}
          </div>

          {/* Предопределенные теги */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Популярные теги:</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {PREDEFINED_TAGS.map(tag => {
                const isSelected = selectedTags.includes(tag.id);
                return (
                  <button
                    key={tag.id}
                    onClick={() => toggleTag(tag.id)}
                    disabled={!isSelected && selectedTags.length >= 3}
                    className={`p-4 rounded-lg border-2 transition-all text-left ${
                      isSelected
                        ? 'border-current shadow-md transform scale-105'
                        : 'border-gray-200 hover:border-gray-300'
                    } ${!isSelected && selectedTags.length >= 3 ? 'opacity-50 cursor-not-allowed' : ''}`}
                    style={{
                      backgroundColor: isSelected ? `${tag.color}15` : 'white',
                      borderColor: isSelected ? tag.color : undefined
                    }}
                  >
                    <div className="flex items-center mb-2">
                      <span className="text-2xl mr-2">{tag.icon}</span>
                      <span 
                        className="font-semibold"
                        style={{ color: isSelected ? tag.color : '#1F2937' }}
                      >
                        {tag.name}
                      </span>
                      {isSelected && (
                        <svg className="w-5 h-5 ml-auto" fill="currentColor" viewBox="0 0 20 20" style={{ color: tag.color }}>
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                    <p className="text-xs text-gray-500">{tag.description}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Выбранные теги */}
          {selectedTags.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Выбранные теги:</h3>
              <div className="flex flex-wrap gap-2">
                {selectedTags.map(tagId => {
                  const tag = PREDEFINED_TAGS.find(t => t.id === tagId);
                  if (!tag) return null;
                  return (
                    <div
                      key={tagId}
                      className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium"
                      style={{
                        backgroundColor: `${tag.color}20`,
                        color: tag.color,
                        border: `2px solid ${tag.color}`
                      }}
                    >
                      <span className="mr-1">{tag.icon}</span>
                      {tag.name}
                      <button
                        onClick={() => toggleTag(tagId)}
                        className="ml-2 hover:opacity-75"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Кнопки действий */}
          <div className="flex gap-3">
            <button
              onClick={handleSave}
              className="flex-1 px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-colors"
            >
              Сохранить
            </button>
            <button
              onClick={onClose}
              className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-medium transition-colors"
            >
              Отмена
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TagManager;

